import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

const PortalClaimPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ full_name: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(true);

  useEffect(() => {
    const loadInvite = async () => {
      if (!token) {
        toast.error("Missing invite token");
        setLoadingInvite(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/portal/auth/invite/${token}`);
        setInvite(response.data);
        setForm((current) => ({
          ...current,
          full_name: response.data.full_name || "",
          phone: response.data.phone || "",
        }));
      } catch (error) {
        toast.error(error.response?.data?.detail || "Invite not found");
      } finally {
        setLoadingInvite(false);
      }
    };
    loadInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/portal/auth/invite/claim`, {
        token,
        full_name: form.full_name,
        phone: form.phone || null,
        password: form.password,
      });
      localStorage.setItem("portalToken", response.data.token);
      toast.success("Portal access claimed");
      navigate("/portal");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim invite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="w-full max-w-lg border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-2xl"><KeyRound className="h-6 w-6 text-primary" /> Claim Worker Portal Access</CardTitle>
          <CardDescription>
            {invite ? `Set your password to activate portal access for ${invite.company_name}.` : "Loading invite..."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingInvite ? (
            <div className="text-sm text-muted-foreground">Loading invite details...</div>
          ) : !invite ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">This invite is invalid, expired, or already claimed.</p>
              <Link to="/portal/login" className="text-primary hover:underline font-medium">Back to portal login</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label>Email</Label><Input value={invite.email} disabled className="bg-muted/50" /></div>
              <div className="space-y-2"><Label>Full Name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-muted/50" /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-muted/50" /></div>
              <div className="space-y-2"><Label>Password</Label><Input type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="bg-muted/50" /></div>
              <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90">{loading ? "Activating..." : "Claim portal access"}</Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalClaimPage;
